import { useNavbarScroll } from '@/contexts/NavbarScrollContext';
import useCurrencyOptions from '@/hooks/currency/CurrencySymbols';
import { setPreferredCurrency } from '@/pages/Profile/store/actions';
import AutoComplete, { Sizes } from '@components/AutoComplete';
import { Box, Tooltip } from '@mui/material';
import { selectThemeMode } from '@store/app/selectors';
import { updateUserCurrency } from '@store/auth/authSlice';
import { logout } from '@store/auth/thunks';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import React, { useCallback, useMemo, useState } from 'react';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { PagesPermissions } from '../../models/Permissions/enum';

import BackgroundTasksPopover from '../BackgroundTasksPopover';
import { ICONS_TYPES } from '../Icon';
import NavbarSearchPopover from '../NavbarSearchPopover';
import ThemeSwitcher from '../ThemeSwitcher';

import {
  ButtonsContainer,
  CurrencyDropdownContainer,
  Divider,
  NavbarScrolledContentWrapper,
  RightButtonsContainer,
  Spacer,
  StyledAppBar,
  StyledAvatarIcon,
  StyledListItemIcon,
  StyledListItemText,
  StyledMenu,
  StyledMenuItem,
  StyledSubItemIcon,
  StyledToolbar,
  UserFullName,
  UserName,
  UserNameContainer,
} from './styles';
import { CurrencySelectedTypes, Props } from './types';

const TopNavbar = ({ className }: Props) => {
  const dispatch = useAppDispatch();
  const { scrolledContent } = useNavbarScroll();
  const navigate = useNavigate();
  const [refToolMenu, setRefToolMenu] = useState<
    (EventTarget & Element) | null
  >(null);
  const [refProfileMenu, setRefProfileMenu] = useState<
    (EventTarget & Element) | null
  >(null);
  const openedToolMenu = useMemo(() => !!refToolMenu, [refToolMenu]);
  const openedProfileMenu = useMemo(() => !!refProfileMenu, [refProfileMenu]);
  const { isAuthenticated, user, permissions } = useAppSelector(
    (state) => state.auth,
  );
  const themeMode = useSelector(selectThemeMode);
  const { preferredCurrency } = useAppSelector((state) => state.profile);
  const currenciesLoading = useAppSelector(
    (state) => state.global.currenciesLoading,
  );
  const currencyOptions = useCurrencyOptions();
  const [selectedCurrency, setSelectedCurrency] =
    useState<CurrencySelectedTypes | null>(null);
  const hasPermissionForProfilePage = useMemo(
    () => permissions.includes(PagesPermissions.userProfile),
    [permissions],
  );
  const hasPermissionForApiDocs = useMemo(
    () => permissions.includes(PagesPermissions.clientApiDocs),
    [permissions],
  );
  const hasPermissionForBackgroundTasks = useMemo(
    () => permissions.includes(PagesPermissions.notificTaskProgress),
    [permissions],
  );

  const onChangeCurrency = (value: CurrencySelectedTypes | null) => {
    setSelectedCurrency(value);
    if (value) {
      // 1. Update UI state
      dispatch(setPreferredCurrency({ currency: value }));

      // 2. Update auth state (used by Dashboard)
      dispatch(updateUserCurrency(value.id));
    }
  };

  const handleCloseToolsMenu = useCallback(() => setRefToolMenu(null), []);
  const handleOpenProfileMenu = useCallback(
    (e: React.MouseEvent<Element, MouseEvent>) =>
      setRefProfileMenu(e.currentTarget),
    [],
  );
  const handleCloseProfileMenu = useCallback(() => setRefProfileMenu(null), []);
  const handleLogout = useCallback(() => dispatch(logout()), [dispatch]);
  const handleClickChangeRequest = useCallback(() => {
    handleCloseProfileMenu();
    window.open(
      'https://pmctreasury.teamwork.com/app/public/forms/WGVeNqjcVOWPOwZBqNWL',
    );
  }, [handleCloseProfileMenu]);
  const handleClickProfile = useCallback(
    () => hasPermissionForProfilePage && navigate('/profile'),
    [hasPermissionForProfilePage, navigate],
  );

  const handleTermsOfBusiness = useCallback(
    () => navigate('/terms'),
    [navigate],
  );

  const handleTutorials = useCallback(() => navigate('/tutorials'), [navigate]);

  const handleApiDocs = useCallback(() => navigate('/api-docs'), [navigate]);

  useEffect(() => {
    if (!currencyOptions.length) return;

    // Always sync with auth.user.currencyId (from login response)
    // If user logged in or currencyId changed, update preferredCurrency
    if (user.currencyId) {
      const foundCurrency =
        currencyOptions.find((c) => c.id === user.currencyId) || null;

      if (foundCurrency && foundCurrency.id !== preferredCurrency?.id) {
        setSelectedCurrency(foundCurrency);
        dispatch(setPreferredCurrency({ currency: foundCurrency }));
      } else if (foundCurrency && preferredCurrency) {
        // Currency already matches, just update local state
        setSelectedCurrency(preferredCurrency);
      }
    }
  }, [currencyOptions, user.currencyId, preferredCurrency, dispatch]);

  return (
    <StyledAppBar className={className}>
      <StyledToolbar>
        {/* <HomeLinkContainer>
          <StyledHomeLink to="/">
            <LogoIcon
              icon={
                themeMode === ThemeModes.light
                  ? ICONS_TYPES.LogoDarkBlue
                  : ICONS_TYPES.LogoOrange
              }
              size={130}
            />
          </StyledHomeLink>
        </HomeLinkContainer> */}
        {isAuthenticated && (
          <ButtonsContainer>
            <StyledMenu
              anchorEl={refToolMenu}
              open={openedToolMenu}
              onClose={handleCloseToolsMenu}
              MenuListProps={{
                'aria-labelledby': 'tools-button',
              }}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}></StyledMenu>
          </ButtonsContainer>
        )}
        {scrolledContent ? (
          <NavbarScrolledContentWrapper>
            {scrolledContent}
          </NavbarScrolledContentWrapper>
        ) : (
          <Spacer />
        )}
        <RightButtonsContainer>
          <CurrencyDropdownContainer>
            <AutoComplete
              label="Currency"
              options={currencyOptions}
              value={selectedCurrency}
              onChange={(newValue) => onChangeCurrency(newValue)}
              placeholder="Select Currency"
              size={Sizes.small}
              loading={currenciesLoading}
              disableClearable={true}
              name={'Currency'}
            />
          </CurrencyDropdownContainer>
          <NavbarSearchPopover />
          {isAuthenticated && hasPermissionForBackgroundTasks && (
            <BackgroundTasksPopover />
          )}
          <ThemeSwitcher />
          {isAuthenticated && (
            <>
              <Tooltip title="Account settings">
                <StyledAvatarIcon onClick={handleOpenProfileMenu}>
                  {user?.firstName ? user.firstName.charAt(0) : 'U'}
                </StyledAvatarIcon>
              </Tooltip>
              <StyledMenu
                anchorEl={refProfileMenu}
                open={openedProfileMenu}
                onClose={handleCloseProfileMenu}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>
                <UserNameContainer
                  onClick={handleClickProfile}
                  hasPermissionForProfilePage={hasPermissionForProfilePage}>
                  <StyledAvatarIcon>
                    {user?.firstName ? user.firstName.charAt(0) : 'U'}
                  </StyledAvatarIcon>
                  <Box>
                    <UserName>{user?.firstName || ''}</UserName>
                    <UserFullName>{user?.fullName || ''}</UserFullName>
                  </Box>
                </UserNameContainer>
                <Divider />
                <StyledMenuItem onClick={handleClickChangeRequest}>
                  <StyledListItemIcon>
                    <StyledSubItemIcon
                      icon={ICONS_TYPES.MessageQuestion}
                      size={20}
                    />
                  </StyledListItemIcon>
                  <StyledListItemText primary={'Change Request'} />
                </StyledMenuItem>
                <Divider />
                <StyledMenuItem onClick={handleTermsOfBusiness}>
                  <StyledListItemIcon>
                    <StyledSubItemIcon icon={ICONS_TYPES.Exchange} size={20} />
                  </StyledListItemIcon>
                  <StyledListItemText primary={'Terms and Docs'} />
                </StyledMenuItem>
                <Divider />
                <Divider />
                <StyledMenuItem onClick={handleTutorials}>
                  <StyledListItemIcon>
                    <StyledSubItemIcon icon={ICONS_TYPES.Play} size={20} />
                  </StyledListItemIcon>
                  <StyledListItemText primary={'Tutorials'} />
                </StyledMenuItem>
                {hasPermissionForApiDocs && <Divider />}
                {hasPermissionForApiDocs && (
                  <StyledMenuItem onClick={handleApiDocs}>
                    <StyledListItemIcon>
                      <StyledSubItemIcon icon={ICONS_TYPES.Article} size={20} />
                    </StyledListItemIcon>
                    <StyledListItemText primary={'API Documentation'} />
                  </StyledMenuItem>
                )}
                <Divider />
                <StyledMenuItem onClick={handleLogout}>
                  <StyledListItemIcon>
                    <StyledSubItemIcon icon={ICONS_TYPES.Logout} size={20} />
                  </StyledListItemIcon>
                  <StyledListItemText primary={'Logout'} />
                </StyledMenuItem>
              </StyledMenu>
            </>
          )}
        </RightButtonsContainer>
      </StyledToolbar>
    </StyledAppBar>
  );
};

export default TopNavbar;
